import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, Outlet } from "react-router-dom";
import { IntlProvider } from "react-intl";
import { useState, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import GoogleAnalytics from "./components/GoogleAnalytics";
import Home from "./pages/Home";
import HowToPlay from "./pages/HowToPlay";
import Privacy from "./pages/Privacy";
import About from "./pages/About";
import ContactUs from "./pages/ContactUs";
import PokeRogue from "./pages/PokeRogue";
import StickmanHook from "./pages/StickmanHook";
import MonkeyMart from "./pages/MonkeyMart";
import Vex5 from "./pages/Vex5";
import ColorTunnel from "./pages/ColorTunnel";
import CapybaraClicker from "./pages/CapybaraClicker";
import PokemonClicker from "./pages/PokemonClicker";
import PokemonClicker2 from "./pages/PokemonClicker2";
import TimeShooter3 from "./pages/TimeShooter3";
import NotFound from "./pages/NotFound";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { I18N_ROUTES, NON_I18N_ROUTES, SUPPORTED_LANGUAGES, needsI18nRoute } from "./config/i18nRoutes";
import enMessages from "./locales/en.json";
import zhMessages from "./locales/zh.json";
import koMessages from "./locales/ko.json";
import esMessages from "./locales/es.json";
import frMessages from "./locales/fr.json";
import ptMessages from "./locales/pt.json";
import daMessages from "./locales/da.json";
import plMessages from "./locales/pl.json";
import ruMessages from "./locales/ru.json";
import viMessages from "./locales/vi.json";
import thMessages from "./locales/th.json";
import filMessages from "./locales/fil.json";
import jaMessages from "./locales/ja.json";
import noMessages from "./locales/no.json";
import nlMessages from "./locales/nl.json";
import deMessages from "./locales/de.json";
import itMessages from "./locales/it.json";
import hiMessages from "./locales/hi.json";
import arMessages from "./locales/ar.json";

const messages = {
  en: enMessages,
  zh: zhMessages,
  ko: koMessages,
  es: esMessages,
  fr: frMessages,
  pt: ptMessages,
  da: daMessages,
  pl: plMessages,
  ru: ruMessages,
  vi: viMessages,
  th: thMessages,
  fil: filMessages,
  ja: jaMessages,
  no: noMessages,
  nl: nlMessages,
  de: deMessages,
  it: itMessages,
  hi: hiMessages,
  ar: arMessages
};

const queryClient = new QueryClient();

const App = () => {
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage || navigator.language.split('-')[0] || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  return (
    <IntlProvider messages={messages[language]} locale={language} defaultLocale="en">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <HelmetProvider>
            <BrowserRouter>
              <GoogleAnalytics />
              <div className="flex flex-col min-h-screen">
                <Header language={language} setLanguage={setLanguage} />
                <main className="flex-grow">
                  <Routes>
                    {/* 有多语言支持的页面路由 */}
                    <Route path="/" element={<LanguageRouter language={language} setLanguage={setLanguage} />}>
                      <Route index element={<Home />} />
                      <Route path="how-to-play" element={<HowToPlay />} />
                      <Route path="privacy" element={<Privacy />} />
                      <Route path="about" element={<About />} />
                      <Route path="contact" element={<ContactUs />} />
                    </Route>
                    
                    {/* 没有多语言支持的页面路由 - 直接访问，不走多语言路由 */}
                    <Route path="pokerogue" element={<PokeRogue />} />
                    <Route path="stickman-hook" element={<StickmanHook />} />
                    <Route path="monkey-mart" element={<MonkeyMart />} />
                    <Route path="vex5" element={<Vex5 />} />
                    <Route path="color-tunnel" element={<ColorTunnel />} />
                    <Route path="capybara-clicker" element={<CapybaraClicker />} />
                    <Route path="pokemon-clicker" element={<PokemonClicker />} />
                    <Route path="pokemon-clicker-2" element={<PokemonClicker2 />} />
                    <Route path="time-shooter-3" element={<TimeShooter3 />} />
                    
                    {/* 多语言路由 - 只包含有多语言支持的页面 */}
                    {SUPPORTED_LANGUAGES.map(lang => (
                      <Route key={lang} path={`/${lang}/*`} element={<LanguageRouter language={language} setLanguage={setLanguage} />}>
                        <Route index element={<Home />} />
                        <Route path="how-to-play" element={<HowToPlay />} />
                        <Route path="privacy" element={<Privacy />} />
                        <Route path="about" element={<About />} />
                        <Route path="contact" element={<ContactUs />} />
                      </Route>
                    ))}

                    

                    
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
                <Footer />
              </div>
            </BrowserRouter>
          </HelmetProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </IntlProvider>
  );
};

const LanguageRouter = ({ language, setLanguage }) => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const pathname = window.location.pathname;

  useEffect(() => {
    if ((lang === 'zh' || lang === 'ko' || lang === 'es' || lang === 'fr' || lang === 'pt' || lang === 'da' || lang === 'pl' || lang === 'ru' || lang === 'vi' || lang === 'th' || lang === 'fil' || lang === 'ja' || lang === 'no' || lang === 'nl' || lang === 'de' || lang === 'it' || lang === 'hi' || lang === 'ar') && language !== lang) {
      setLanguage(lang);
    } else if (!pathname.startsWith('/zh') && !pathname.startsWith('/ko') && !pathname.startsWith('/es') && !pathname.startsWith('/fr') && !pathname.startsWith('/pt') && !pathname.startsWith('/da') && !pathname.startsWith('/pl') && !pathname.startsWith('/ru') && !pathname.startsWith('/vi') && !pathname.startsWith('/th') && !pathname.startsWith('/fil') && !pathname.startsWith('/ja') && !pathname.startsWith('/no') && !pathname.startsWith('/nl') && !pathname.startsWith('/de') && !pathname.startsWith('/it') && !pathname.startsWith('/hi') && !pathname.startsWith('/ar') && language !== 'en') {
      navigate('/' + language + pathname, { replace: true });
    } else if ((pathname.startsWith('/zh') || pathname.startsWith('/ko') || pathname.startsWith('/es') || pathname.startsWith('/fr') || pathname.startsWith('/pt') || pathname.startsWith('/da') || pathname.startsWith('/pl') || pathname.startsWith('/ru') || pathname.startsWith('/vi') || pathname.startsWith('/th') || pathname.startsWith('/fil') || pathname.startsWith('/ja') || pathname.startsWith('/no') || pathname.startsWith('/nl') || pathname.startsWith('/de') || pathname.startsWith('/it') || pathname.startsWith('/hi') || pathname.startsWith('/ar')) && language === 'en') {
      navigate(pathname.replace(/^\/[a-z]{2,3}/, ''), { replace: true });
    }
  }, [lang, language, setLanguage, navigate, pathname]);

  return <Outlet />;
};

export default App;
